import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import * as Query from '../../../api/gql/query'
import { formatIsoDate } from '../../schedule/lib/format'
import { dueState, pendingPath } from '../lib/applications'
import { describeCostControlError } from '../lib/errors'
import { todayIso } from '../lib/invoices'
import { formatMoney, moneyLocale } from '../lib/money'
import type { CostControlView, CostPendingAction } from '../types'
import PendingTabSkeleton from './PendingTabSkeleton'

const TYPE_KEY: Record<CostPendingAction['type'], string> = {
  APPLICATION: 'CostControl_pending_type_APPLICATION',
  CHANGE: 'CostControl_pending_type_CHANGE',
  INVOICE: 'CostControl_pending_type_INVOICE',
}
const STATE_KEY: Record<CostPendingAction['state'], string> = {
  SUBMITTED: 'CostControl_pending_state_SUBMITTED',
  RETURNED: 'CostControl_pending_state_RETURNED',
  PENDING: 'CostControl_pending_state_PENDING',
}

/** Everything waiting for a decision, oldest first (1b-A spec §9). */
const PendingTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { data, loading, error } = useQuery<{ costPendingActions: CostPendingAction[] }>(Query.COST_PENDING_ACTIONS, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: viewData } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    fetchPolicy: 'cache-first',
    context: { handlesErrors: true },
  })
  const currency = viewData?.costControl.settings.currency ?? 'CZK'
  const rows = data?.costPendingActions
  const today = todayIso()
  const label = (a: CostPendingAction) => (a.type === 'APPLICATION' ? t('CostControl_application_title', { number: a.number })
    : a.type === 'CHANGE' ? `${t('CostControl_change_number', { number: a.number })} ${a.title ?? ''}`.trim()
      : t('CostControl_invoice_title', { number: a.number }))

  if (!rows && loading) return <PendingTabSkeleton />
  if (!rows && error) return <p className="py-4 text-sm text-destructive">{describeCostControlError(error.message, t)}</p>
  if (!rows || rows.length === 0) return <p className="py-8 text-center text-sm text-muted-foreground">{t('CostControl_pending_empty')}</p>
  return (
    <div className="py-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-32">{t('CostControl_pending_col_type')}</TableHead>
            <TableHead>{t('CostControl_pending_col_record')}</TableHead>
            <TableHead>{t('CostControl_pending_col_contract')}</TableHead>
            <TableHead className="w-36 text-right">{t('CostControl_pending_col_amount')}</TableHead>
            <TableHead className="w-32">{t('CostControl_pending_col_since')}</TableHead>
            <TableHead className="w-40">{t('CostControl_pending_col_by')}</TableHead>
            <TableHead className="w-56">{t('CostControl_pending_col_state')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((a) => {
            const due = a.type === 'INVOICE' ? dueState(a.dueDate, today) : null
            return (
              <TableRow key={`${a.type}:${a.id}`}>
                <TableCell><Badge variant="secondary">{t(TYPE_KEY[a.type])}</Badge></TableCell>
                <TableCell><Link className="text-primary hover:underline" to={pendingPath(projectId, a)}>{label(a)}</Link></TableCell>
                <TableCell className="text-sm">{a.contractName ?? '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{formatMoney(a.amount, { locale, currency })}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">{formatIsoDate(t, a.since.slice(0, 10))}</TableCell>
                <TableCell className="text-sm">{a.byName ?? '—'}</TableCell>
                <TableCell className="space-x-1">
                  <Badge variant={a.state === 'RETURNED' ? 'danger' : 'warning'}>{t(STATE_KEY[a.state])}</Badge>
                  {due === 'overdue' && <Badge variant="danger">{t('CostControl_pending_overdue')}</Badge>}
                  {due === 'soon' && <Badge variant="warning">{t('CostControl_pending_due_soon')}</Badge>}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
export default PendingTab
